import React, {
  useState,
  useEffect,
  useCallback
} from 'react'
import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Input,
  Table
} from 'reactstrap'
import { Formik } from 'formik'
import { useHistory, useParams } from 'react-router'
import { ALERT_TYPES } from 'constants/alert-constants'
import TitlePage from 'components/atoms/title-page'
import InputField from 'components/molecules/input-field'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import InternalRequestDetail from 'domains/internal-request/organisms/internal-request-detail'
import InternalRequestItemTable from 'domains/internal-request/organisms/item-table'
import InventorySelectSearchField from 'domains/internal-request/molecules/inventory-select-search-field'
import OutboundInternalRequestsApiV2 from 'api/v2/admins/outbound-internal-requests-api-v2'
import PropTypes from 'prop-types'


const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const InternalRequestRequestorNewDetailPage = ({ pageUtils }) => {
  const [ internalRequest, setInternalRequest ] = useState({})
  const [ items, setItems ]                     = useState([])
  const [ pagination, setPagination ]           = useState({})
  const [ isCancelModal, setIsCancelModal ]     = useState(false)
  const [ isEditMode, setIsEditMode ]           = useState(false)
  const [ isSavingItems, setIsSavingItems ]     = useState(false)
  const [ editableItems, setEditableItems ]     = useState([])
  const [ isRemoveItemModal, setIsRemoveItemModal ] = useState(false)
  const [ pendingRemoveItem, setPendingRemoveItem ] = useState(null)
  const history                                  = useHistory()
  const { id }                                   = useParams()

  // Hide particular columns
  const hiddenColumns = [
    'status',
    'borrowed',
    'marked_for_return',
    'quantity_put_away',
    'quantity_requested',
    'quantity_picked',
    'returned',
    'reject_reason',
    'action'
  ]

  const cancelModalToggle = () => {
    setIsCancelModal(!isCancelModal)
  }

  const handleFetchInternalRequest = useCallback(() => {
    return OutboundInternalRequestsApiV2.show(id)
      .then((response) => {
        setInternalRequest(response.data.internal_request)
        return response.data.internal_request
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ id, pageUtils ])

  /** Fetching Internal Request Item */
  const handleFetchItems = useCallback((tableState) => {
    return OutboundInternalRequestsApiV2.getItems({ internal_request_id: id, tableState: tableState })
      .then((response) => {
        setItems(response.data.internal_request_items)
        setPagination(response.data.meta)
        return response.data.internal_request_items
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ id, pageUtils ])

  /** Handler to cancel request */
  const handleCancelOutboundInternalRequest = async () => {
    await OutboundInternalRequestsApiV2.cancel(id)
      .then(() => pageUtils.setAlertMsg('Internal Request Successfully Canceled', ALERT_TYPES.SUCCESS))
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => {
        cancelModalToggle()
        history.goBack()
      })
  }

  const mapItemToEditableInventory = (item) => ({
    id                : item.inventory?.id,
    inventory_id      : item.inventory?.id,
    inventory_sku     : item.inventory?.inventory_sku,
    product_name      : item.inventory?.product?.name,
    product_size      : item.inventory?.product_dimension?.size,
    stored_quantity   : Number(item.inventory?.stored_quantity || 0),
    original_quantity : Number(item.quantity || 0),
    quantity          : Number(item.quantity || 0)
  })

  const handleStartEdit = async () => {
    if (!internalRequest.can_edit_items) {
      pageUtils.setAlertMsg('Item hanya bisa diedit sebelum proses warehouse dimulai', ALERT_TYPES.WARNING)
      return
    }

    await OutboundInternalRequestsApiV2.getItems({
      internal_request_id : id,
      additionalFilters   : { per_page: 1000 }
    })
      .then((response) => {
        setEditableItems((response.data.internal_request_items || []).map(mapItemToEditableInventory))
        setIsEditMode(true)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }

  const handleCancelEdit = () => {
    setEditableItems([])
    setIsEditMode(false)
  }

  const handleRequestRemoveEditableItem = (index) => {
    if (editableItems.length <= 1) {
      pageUtils.setAlertMsg('Internal Request harus memiliki minimal 1 item', ALERT_TYPES.WARNING)
      return
    }

    setPendingRemoveItem(editableItems[index])
    setIsRemoveItemModal(true)
  }

  const handleRemoveEditableItem = () => {
    if (!pendingRemoveItem) return

    const removedSku = pendingRemoveItem.inventory_sku || 'SKU'

    setEditableItems((currentItems) =>
      currentItems.filter((item) => item.inventory_id !== pendingRemoveItem.inventory_id)
    )
    pageUtils.setAlertMsg(`${removedSku} berhasil di-remove dari list`, ALERT_TYPES.SUCCESS)
    setPendingRemoveItem(null)
    setIsRemoveItemModal(false)
  }

  const handleCancelRemoveEditableItem = () => {
    setPendingRemoveItem(null)
  }

  const handleEditableQuantityChange = (index, value) => {
    const quantity = parseInt(value, 10)

    setEditableItems(editableItems.map((item, itemIndex) => {
      if (itemIndex !== index) return item

      return {
        ...item,
        quantity: Number.isNaN(quantity) ? '' : quantity
      }
    }))
  }

  const handleAddEditableItem = (formikBag) => {
    const selectedInventory = formikBag.values.selected_inventory
    const quantity = parseInt(formikBag.values.selected_inventory_quantity || 1, 10)

    if (!selectedInventory || !selectedInventory.id) {
      pageUtils.setAlertMsg('Pilih inventory terlebih dahulu', ALERT_TYPES.WARNING)
      return
    }

    if (!quantity || quantity <= 0) {
      pageUtils.setAlertMsg('Borrow quantity harus lebih dari 0', ALERT_TYPES.WARNING)
      return
    }

    const storedQuantity = Number(selectedInventory.stored_quantity || selectedInventory.quantity || 0)

    if (storedQuantity <= 0) {
      pageUtils.setAlertMsg('Inventory ini tidak memiliki quantity yang bisa dipinjam', ALERT_TYPES.WARNING)
      return
    }

    if (quantity > storedQuantity) {
      pageUtils.setAlertMsg(`Borrow quantity maksimal ${storedQuantity}`, ALERT_TYPES.WARNING)
      return
    }

    if (editableItems.some((item) => item.inventory_id === selectedInventory.id)) {
      const duplicateSku = selectedInventory.inventory_sku || 'SKU ini'
      pageUtils.setAlertMsg(`${duplicateSku} sudah ada di list`, ALERT_TYPES.WARNING)
      return
    }

    setEditableItems([ ...editableItems, {
      id                : selectedInventory.id,
      inventory_id      : selectedInventory.id,
      inventory_sku     : selectedInventory.inventory_sku,
      product_name      : selectedInventory.product_name,
      product_size      : selectedInventory.product_size,
      stored_quantity   : storedQuantity,
      original_quantity : 0,
      quantity          : quantity
    } ])

    pageUtils.setAlertMsg(
      `${selectedInventory.inventory_sku || 'SKU'} berhasil ditambahkan ke list`,
      ALERT_TYPES.SUCCESS
    )
    formikBag.setFieldValue('selected_inventory', '')
    formikBag.setFieldValue('selected_inventory_quantity', 1)
  }

  const handleSaveItems = async () => {
    if (editableItems.length === 0) {
      pageUtils.setAlertMsg('Internal Request harus memiliki minimal 1 item', ALERT_TYPES.WARNING)
      return
    }

    const invalidItem = editableItems.find((item) => {
      const quantity = Number(item.quantity || 0)
      const maxQuantity = Math.max(Number(item.stored_quantity || 0), Number(item.original_quantity || 0))

      return quantity <= 0 || quantity > maxQuantity
    })

    if (invalidItem) {
      pageUtils.setAlertMsg(`Quantity ${invalidItem.inventory_sku} tidak valid`, ALERT_TYPES.WARNING)
      return
    }

    setIsSavingItems(true)

    await OutboundInternalRequestsApiV2.updateItems(id, editableItems)
      .then(async () => {
        pageUtils.setAlertMsg('Internal Request items successfully updated', ALERT_TYPES.SUCCESS)
        setIsEditMode(false)
        setEditableItems([])
        await Promise.all([ handleFetchInternalRequest(), handleFetchItems() ])
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => setIsSavingItems(false))
  }

  useEffect(() => {
    handleFetchInternalRequest()
  }, [ handleFetchInternalRequest ])

  useEffect(() => {
    handleFetchItems()
  }, [ handleFetchItems ])

  return (
    <>
      <TitlePage mainTitle='Internal Request' subTitle='New Detail page' />

      <InternalRequestDetail internal_request={ internalRequest } />

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{ 'Requested Items' }</h2>
          </CardTitle>
          <Button
            color='info'
            onClick={ () => { history.goBack()} }
          >
            { 'Back' }
          </Button>
          <Button
            className='float-right'
            color='danger'
            disabled={ isEditMode }
            onClick={ () => cancelModalToggle() }
          >
            { 'Cancel' }
          </Button>
          { !isEditMode && (
            <Button
              className='float-right mr-2'
              color='primary'
              disabled={ !internalRequest.can_edit_items }
              onClick={ handleStartEdit }
            >
              { 'Edit Request Items' }
            </Button>
          ) }
        </CardHeader>
        <CardBody>
          { !internalRequest.can_edit_items && !isEditMode && internalRequest.status === 'requested' && (
            <Alert color='warning'>
              { 'Item tidak bisa diedit karena proses warehouse sudah dimulai.' }
            </Alert>
          ) }

          { isEditMode ? (
            <>
              <Alert color='info'>
                { 'Tambah atau hapus SKU, atau ubah quantity. Perubahan baru tersimpan setelah klik Save Changes.' }
              </Alert>

              <Formik
                initialValues={{
                  selected_inventory          : '',
                  selected_inventory_quantity : 1
                }}
                onSubmit={ () => {} }
              >
                { (formikBag) => (
                  <div className='mb-4'>
                    <InventorySelectSearchField
                      name='selected_inventory'
                      label='Cari Inventory'
                      required={ false }
                    />
                    <InputField
                      type='number'
                      name='selected_inventory_quantity'
                      label='Borrow Quantity'
                      min={ 1 }
                      required={ false }
                    />
                    <Button
                      color='primary'
                      type='button'
                      onClick={ () => handleAddEditableItem(formikBag) }
                    >
                      { 'Add Item' }
                    </Button>
                  </div>
                ) }
              </Formik>

              <div className='table-responsive'>
                <Table bordered>
                  <thead>
                    <tr>
                      <th>{ 'No.' }</th>
                      <th>{ 'Inventory SKU' }</th>
                      <th>{ 'Name' }</th>
                      <th style={{ width: '180px' }}>{ 'Borrow Quantity' }</th>
                      <th>{ 'Action' }</th>
                    </tr>
                  </thead>
                  <tbody>
                    { editableItems.map((item, index) => {
                      const maxQuantity = Math.max(Number(item.stored_quantity || 0), Number(item.original_quantity || 0))

                      return (
                        <tr key={ item.inventory_id }>
                          <td>{ index + 1 }</td>
                          <td>{ item.inventory_sku }</td>
                          <td>{ item.product_name }</td>
                          <td>
                            <Input
                              type='number'
                              min={ 1 }
                              max={ maxQuantity }
                              value={ item.quantity }
                              onChange={ (event) => handleEditableQuantityChange(index, event.target.value) }
                            />
                            <small className='text-muted'>
                              { `Max: ${maxQuantity}` }
                            </small>
                          </td>
                          <td>
                            <Button
                              color='danger'
                              type='button'
                              onClick={ () => handleRequestRemoveEditableItem(index) }
                            >
                              { 'Remove' }
                            </Button>
                          </td>
                        </tr>
                      )
                    }) }
                  </tbody>
                </Table>
              </div>

              <div className='d-flex justify-content-end'>
                <Button
                  className='mr-2'
                  color='secondary'
                  disabled={ isSavingItems }
                  onClick={ handleCancelEdit }
                >
                  { 'Discard Changes' }
                </Button>
                <Button
                  color='success'
                  disabled={ isSavingItems }
                  onClick={ handleSaveItems }
                >
                  { isSavingItems ? 'Saving...' : 'Save Changes' }
                </Button>
              </div>
            </>
          ) : (
            <InternalRequestItemTable
              data={ items }
              pagination={ pagination }
              onFetchData={ handleFetchItems }
              hiddenColumns={ hiddenColumns }
            />
          ) }
        </CardBody>
      </Card>

      <ConfirmationModal
        title={ 'Remove SKU' }
        isOpen={ isRemoveItemModal }
        toggle={ () => setIsRemoveItemModal(!isRemoveItemModal) }
        onCancel={ handleCancelRemoveEditableItem }
        handleConfirmation={ handleRemoveEditableItem }
      >
        { `Are you sure want to remove ${pendingRemoveItem?.inventory_sku || 'this SKU'} from the list?` }
      </ConfirmationModal>

      <ConfirmationModal
        title={ 'Cancel Internal Request' }
        isOpen={ isCancelModal }
        toggle={ cancelModalToggle }
        handleConfirmation= { () => handleCancelOutboundInternalRequest() }
      >
        { 'Are you sure want to cancel this internal request?' }
      </ConfirmationModal>
    </>
  )
}

InternalRequestRequestorNewDetailPage.propTypes = propTypes

export default InternalRequestRequestorNewDetailPage
